<template>
  <div class="workflow-wrap">

    <!-- Contexto general del bloque -->
    <div class="workflow-block-intro mb-6">
      <p class="workflow-block-intro-text">{{ $t('workflow.block_intro') }}</p>
    </div>

    <!-- ── Parte A: Prácticas ───────────────────────── -->
    <v-card outlined class="block-card mb-6">
      <div class="block-card-header">
        <v-icon color="white" class="mr-2">mdi-clipboard-list-outline</v-icon>
        {{ $t('workflow.part_a_title') }}
      </div>
      <div class="pa-5">
        <p class="block-context">{{ $t('workflow.part_a_context') }}</p>
        <p class="block-intro mt-3">{{ $t('workflow.part_a_intro') }}</p>
        <div v-for="(practice, i) in practices" :key="`pa-${i}`" class="practice-row">
          <div class="practice-label-row">
            <p class="practice-label mb-0">
              <span class="practice-num">{{ i + 1 }}</span>
              {{ practice }}
            </p>
            <v-tooltip right max-width="320" color="grey darken-3">
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  v-bind="attrs"
                  v-on="on"
                  small
                  color="grey lighten-1"
                  class="ml-2 flex-shrink-0"
                  style="cursor:pointer"
                >mdi-information-outline</v-icon>
              </template>
              <span class="tooltip-text">{{ partATooltips[i] }}</span>
            </v-tooltip>
          </div>
          <v-radio-group
            v-model="partA[i]"
            row
            dense
            hide-details
            class="mt-1 mb-3"
            :readonly="locked"
            @change="savePartA(i)"
          >
            <v-radio
              v-for="(opt, j) in practiceOptions"
              :key="j"
              :label="opt"
              :value="j"
              class="radio-opt"
            ></v-radio>
          </v-radio-group>
          <v-divider v-if="i < practices.length - 1" class="mb-3"></v-divider>
        </div>
      </div>
    </v-card>

    <!-- ── Parte B: Luchas (opt-in) ────────────────── -->
    <v-card outlined class="block-card mb-6">
      <div class="block-card-header">
        <v-icon color="white" class="mr-2">mdi-comment-question-outline</v-icon>
        {{ $t('workflow.part_b_title') }}
      </div>
      <div class="pa-5">
        <p class="block-context">{{ $t('workflow.part_b_context') }}</p>
        <p class="block-intro mt-3">{{ $t('workflow.part_b_intro') }}</p>
        <div v-for="(struggle, i) in struggles" :key="`pb-${i}`" class="struggle-row mb-4">
          <p class="struggle-label">
            <v-icon x-small color="grey" class="mr-1">mdi-circle-small</v-icon>
            {{ struggle }}
          </p>
          <v-checkbox
            v-for="(opt, j) in struggleOptions"
            :key="j"
            v-model="partB[i]"
            :label="opt"
            :value="j"
            dense
            hide-details
            class="ml-4 mt-0"
            :disabled="locked"
            @change="savePartB(i)"
          ></v-checkbox>
          <!-- Texto libre si se selecciona la última opción -->
          <v-textarea
            v-if="partB[i] && partB[i].includes(struggleOptions.length - 1)"
            v-model="partBFreeText[i]"
            :placeholder="$t('workflow.free_text_placeholder')"
            outlined
            dense
            rows="2"
            class="ml-4 mt-2"
            :disabled="locked"
            @blur="savePartB(i)"
          ></v-textarea>
          <v-divider v-if="i < struggles.length - 1" class="mt-3"></v-divider>
        </div>
      </div>
    </v-card>

    <!-- ── Tres preguntas cortas ───────────────────── -->
    <v-card outlined class="block-card mb-6">
      <div class="block-card-header">
        <v-icon color="white" class="mr-2">mdi-help-circle-outline</v-icon>
        {{ $t('workflow.short_q_title') }}
      </div>
      <div class="pa-5">

        <p class="practice-label mb-2">
          {{ shortQ1Text }}
          <span class="caption grey--text ml-1">{{ $t('workflow.short_q3_max_note') }}</span>
        </p>
        <v-select
          v-model="shortQ1"
          :items="shortQ1Items"
          multiple
          small-chips
          outlined
          dense
          hide-details
          class="mb-5"
          :disabled="locked"
        ></v-select>

        <p class="practice-label mb-2">
          {{ shortQ3Text }}
          <span class="caption grey--text ml-1">{{ $t('workflow.short_q3_max_note') }}</span>
        </p>
        <v-checkbox
          v-for="(opt, i) in shortQ3Opts"
          :key="i"
          v-model="shortQ3"
          :label="opt"
          :value="i"
          dense
          hide-details
          :disabled="locked || shortQ3DisabledAt(i)"
          @change="saveShort(1, shortQ3)"
        ></v-checkbox>
        <!-- Campo libre cuando el último ítem ("Otro") está seleccionado -->
        <v-textarea
          v-if="shortQ3Opts.length && shortQ3.includes(shortQ3Opts.length - 1)"
          v-model="shortQ3OtherText"
          :placeholder="$t('workflow.short_q3_other_placeholder')"
          outlined
          dense
          rows="2"
          class="ml-4 mt-2"
          :disabled="locked"
          @blur="saveShort(2, shortQ3OtherText)"
        ></v-textarea>

        <p class="practice-label mb-2 mt-4">{{ shortQ2Text }}</p>
        <v-select
          v-model="shortQ2"
          :items="shortQ2Items"
          outlined
          dense
          hide-details
          class="mb-2"
          :disabled="locked"
          @change="saveShort(3, shortQ2)"
        ></v-select>

      </div>
    </v-card>

    <div class="d-flex justify-end">
      <v-btn color="primary" elevation="0" :loading="saving" :disabled="locked" @click="saveAll">
        {{ $t('workflow.save_btn') }}
      </v-btn>
    </div>

  </div>
</template>

<script>
import { useMainStore } from '@/store/main';
import { mapStores } from 'pinia';

export default {
  name: 'WorkflowFrictionBlock',
  props: {
    cycleId: { type: Number, required: true },
    locked:  { type: Boolean, default: false },
  },
  data() {
    return {
      saving: false,
      loadingConfig: true,
      blockConfig: { part_a: {}, part_b: {}, short: [] },
      partA: [],
      partB: [],
      partBFreeText: [],
      shortQ1: [],
      shortQ3: [],
      shortQ3OtherText: '',
      shortQ2: null,
    };
  },
  computed: {
    ...mapStores(useMainStore),
    locale() { return this.$i18n.locale; },
    practices()       {
      return this.blockConfig.part_a[`items_${this.locale}`]
        || this.blockConfig.part_a.items_es || [];
    },
    practiceOptions() {
      return this.blockConfig.part_a[`options_${this.locale}`]
        || this.blockConfig.part_a.options_es || [];
    },
    struggles()       {
      return this.blockConfig.part_b[`items_${this.locale}`]
        || this.blockConfig.part_b.items_es || [];
    },
    struggleOptions() {
      return this.blockConfig.part_b[`options_${this.locale}`]
        || this.blockConfig.part_b.options_es || [];
    },
    shortQuestions()  { return this.blockConfig.short || []; },
    partATooltips() {
      return this.practices.map((_, i) => this.$t(`workflow.part_a_help_${i}`));
    },
    shortQ1Text()     { return this.shortQuestions[0]?.[`question_${this.locale}`] || this.shortQuestions[0]?.question_es || ''; },
    shortQ1Opts() {
      const opts = this.shortQuestions[0]?.[`options_${this.locale}`];
      return (opts && opts.length) ? opts : (this.shortQuestions[0]?.options_es || []);
    },
    shortQ1Items() {
      return this.shortQ1Opts.map((text, i) => ({ text, value: i }));
    },
    shortQ3Text()     { return this.shortQuestions[1]?.[`question_${this.locale}`] || this.shortQuestions[1]?.question_es || ''; },
    shortQ3Opts() {
      const opts = this.shortQuestions[1]?.[`options_${this.locale}`];
      return (opts && opts.length) ? opts : (this.shortQuestions[1]?.options_es || []);
    },
    shortQ2Text()     { return this.shortQuestions[2]?.[`question_${this.locale}`] || this.shortQuestions[2]?.question_es || ''; },
    shortQ2Opts() {
      const opts = this.shortQuestions[2]?.[`options_${this.locale}`];
      return (opts && opts.length) ? opts : (this.shortQuestions[2]?.options_es || []);
    },
    shortQ2Items() {
      return this.shortQ2Opts.map((text, i) => ({ text, value: i }));
    },
    isComplete() {
      if (this.loadingConfig) return false;
      const aOk = this.partA.length > 0 && this.partA.every(v => v !== null && v !== undefined);
      const bOk = this.partB.length > 0 && this.partB.every(v => Array.isArray(v) && v.length > 0);
      const sOk = Array.isArray(this.shortQ1) && this.shortQ1.length > 0 &&
                  Array.isArray(this.shortQ3) && this.shortQ3.length > 0 &&
                  this.shortQ2 !== null && this.shortQ2 !== undefined && this.shortQ2 !== '';
      return aOk && bOk && sOk;
    },
  },
  watch: {
    isComplete(val) {
      this.$emit('completion-changed', val);
    },
    shortQ1(val) {
      if (!Array.isArray(val)) return;
      if (val.length > 2) {
        this.shortQ1 = val.slice(val.length - 2);
        return; // watcher re-fires with trimmed value and then saves
      }
      this.saveShort(0, val);
    },
    shortQ3(val) {
      if (!Array.isArray(val)) return;
      const otherIdx = this.shortQ3Opts.length - 1;
      const hasOther = val.includes(otherIdx);
      const regular = val.filter(v => v !== otherIdx);
      if (regular.length <= 2) return;
      const trimmed = regular.slice(regular.length - 2);
      this.shortQ3 = hasOther ? [...trimmed, otherIdx] : trimmed;
    },
  },
  async mounted() {
    await this.loadBlockConfig();
    await this.loadExisting();
    this.$emit('completion-changed', this.isComplete);
  },
  methods: {
    shortQ1DisabledAt(i) {
      return (this.shortQ1 || []).length >= 2 && !this.shortQ1.includes(i);
    },
    shortQ3DisabledAt(i) {
      const otherIdx = this.shortQ3Opts.length - 1;
      if (i === otherIdx) return false; // "Otro" siempre disponible
      const regularSelected = (this.shortQ3 || []).filter(v => v !== otherIdx);
      return regularSelected.length >= 2 && !this.shortQ3.includes(i);
    },
    async loadBlockConfig() {
      try {
        const resp = await this.$axios.get(
          `${process.env.VUE_APP_BACKEND_URL}/workflow/block_config`
        );
        this.blockConfig = resp.data;
        const na = (this.blockConfig.part_a.items_es || []).length || 6;
        const nb = (this.blockConfig.part_b.items_es || []).length || 6;
        this.partA = Array(na).fill(null);
        this.partB = Array(nb).fill(null).map(() => []);
        this.partBFreeText = Array(nb).fill('');
      } catch (e) {
        console.error('[Workflow] Error cargando config desde BD:', e);
        this.partA = Array(6).fill(null);
        this.partB = Array(6).fill(null).map(() => []);
        this.partBFreeText = Array(6).fill('');
      } finally {
        this.loadingConfig = false;
      }
    },

    async loadExisting() {
      try {
        const resp = await this.$axios.get(
          `${process.env.VUE_APP_BACKEND_URL}/workflow/responses`,
          { params: { user_email: this.mainStore.logged_user.email, cycle_id: this.cycleId } }
        );
        resp.data.forEach(r => {
          if (r.question_type === 'part_a') {
            this.$set(this.partA, r.question_index, r.response);
          } else if (r.question_type === 'part_b') {
            this.$set(this.partB, r.question_index, r.response || []);
            this.$set(this.partBFreeText, r.question_index, r.free_text || '');
          } else if (r.question_type === 'short') {
            if (r.question_index === 0) {
              const raw = r.response;
              const arr = Array.isArray(raw) ? raw : (raw !== null && raw !== undefined ? [raw] : []);
              this.shortQ1 = arr.filter(v => typeof v === 'number');
            }
            if (r.question_index === 1) {
              const val = Array.isArray(r.response) ? r.response : [];
              // Only accept numeric indices (new format); ignore legacy text-string saves
              this.shortQ3 = val.filter(v => typeof v === 'number');
            }
            if (r.question_index === 2) this.shortQ3OtherText = r.response || '';
            if (r.question_index === 3) this.shortQ2 = (typeof r.response === 'number') ? r.response : null;
          }
        });
      } catch (e) { void e; }
    },
    async savePartA(idx) {
      await this.persist('part_a', idx, this.partA[idx], null);
    },
    async savePartB(idx) {
      const freeText = this.partBFreeText[idx] || null;
      await this.persist('part_b', idx, this.partB[idx], freeText);
    },
    async saveShort(idx, value) {
      await this.persist('short', idx, value, null);
    },
    async persist(type, idx, value, freeText) {
      try {
        await this.$axios.post(`${process.env.VUE_APP_BACKEND_URL}/workflow/save_response`, {
          user_email:     this.mainStore.logged_user.email,
          cycle_id:       this.cycleId,
          question_type:  type,
          question_index: idx,
          response:       value,
          free_text:      freeText,
        });
      } catch (e) { console.error(e); }
    },
    async saveAll() {
      this.saving = true;
      await Promise.all([
        ...this.partA.map((v, i) => this.persist('part_a', i, v, null)),
        ...this.partB.map((v, i) => this.persist('part_b', i, v, this.partBFreeText[i] || null)),
        this.persist('short', 0, this.shortQ1, null),
        this.persist('short', 1, this.shortQ3, null),
        this.persist('short', 2, this.shortQ3OtherText, null),
        this.persist('short', 3, this.shortQ2, null),
      ]);
      this.saving = false;
      this.mainStore.setFlashMessage({ message: this.$t('workflow.saved_ok'), type: 'success' });
    }
  }
}
</script>

<style scoped>
.workflow-wrap { padding: 4px 0 24px; }

.workflow-block-intro {
  background: #f0f4f8;
  border-left: 4px solid #1e3a5f;
  border-radius: 0 8px 8px 0;
  padding: 14px 18px;
}
.workflow-block-intro-text {
  font-size: 0.875rem;
  color: #374151;
  line-height: 1.65;
  margin: 0;
}

.block-card { border-radius: 10px !important; overflow: hidden; }

.block-card-header {
  background: #1e3a5f;
  color: white;
  padding: 12px 20px;
  font-weight: 600;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
}

.block-context {
  font-size: 0.875rem;
  color: #374151;
  line-height: 1.6;
  background: #f8fafc;
  border-left: 3px solid #cbd5e1;
  padding: 10px 14px;
  border-radius: 0 6px 6px 0;
  margin-bottom: 4px;
}
.block-intro { font-size: 0.875rem; color: #4a5568; margin-bottom: 16px; }

.practice-row { margin-bottom: 4px; }
.practice-label-row {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin-bottom: 4px;
}
.practice-label-row .practice-label { flex: 1; margin-bottom: 0; }
.tooltip-text { font-size: 0.8rem; line-height: 1.45; }
.practice-label {
  font-size: 0.875rem;
  color: #1f2937;
  font-weight: 500;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 4px;
}
.practice-num {
  min-width: 22px; height: 22px;
  background: #e5e7eb;
  color: #374151;
  border-radius: 50%;
  font-size: 0.68rem;
  font-weight: 700;
  display: flex; align-items: center; justify-content: center;
  margin-top: 1px;
  flex-shrink: 0;
}
.radio-opt { margin-right: 16px !important; }

.struggle-row {}
.struggle-label {
  font-size: 0.875rem;
  color: #1f2937;
  font-weight: 500;
  display: flex;
  align-items: flex-start;
  margin-bottom: 4px;
}
</style>
